<template>
  <form class="create-or-edit-tool-form" @submit.prevent.stop="submit">
    <div class="form-content">
      <div class="form-row">
        <p-text-field
          v-model="localItem.description"
          :disabled="!$hasPermission('prompts.write')"
          :label="'Description (how the model should use this tool)'"
          :multiline="true"
          :rows="3"
        />
      </div>
      <div class="form-row">
        <p-text-field
          v-model="localItem.action"
          :disabled="!$hasPermission('prompts.write')"
          :label="'Action message (what user will see when the tool is invoked)'"
        />
      </div>
      <div class="form-row">
        <p-checkbox
          v-model="localItem.cache"
          :disabled="!$hasPermission('prompts.write')"
          :label="'Cache result (should store result in conversation history)'"
        />
      </div>
      <div class="form-row">
        <Multiselect
          v-model="localItem.organizations"
          :options="organizationsOptions"
          :get-label-callback="getOrganizationName"
          :disabled="!$hasPermission('prompts.write')"
          label="Organizations"
          placeholder="Select organizations (optional)"
          :searchable="true"
          :multiple="true"
        ></Multiselect>
      </div>
      <div class="form-row">
        <Multiselect
          v-model="localItem.tags"
          :multiple="true"
          :disabled="!$hasPermission('prompts.write')"
          :taggable="true"
          label="Tags"
          placeholder="Select tags (optional)"
          tag-placeholder=""
          @tag="tagAdded"
        />
      </div>
      <div class="form-row">
        <Multiselect
          v-model="localItem.models"
          :options="modelOptions"
          :get-label-callback="getModelLabel"
          :disabled="!$hasPermission('prompts.write')"
          label="Models"
          placeholder="Select models (optional)"
          :searchable="true"
          :multiple="true"
        ></Multiselect>
      </div>
      <div class="form-row">
        <p-text-field
          v-model="localItem.systemPrompt"
          :disabled="!$hasPermission('prompts.write')"
          :label="'Additional System Prompt (optional)'"
          :multiline="true"
          :rows="3"
        />
      </div>
      <div class="form-row code">
        <label>Tool Parameters</label>
        <p-json-tree v-if="hasParameters" ref="jsonTree" :value="localItem.parameters" :read-only="!$hasPermission('prompts.write')"></p-json-tree>
        <div v-else class="no-parameters-placeholder">No parameters defined for this tool</div>
      </div>
      <div v-if="isRequestFailed" class="error">Failed to save tool. Check your input and try again.</div>
    </div>
    <div class="button-container">
      <p-button type="button" @click.prevent="onCancel">Cancel</p-button>
      <p-button v-if="$hasPermission('prompts.write')" color="primary" type="submit" :disabled="isRequestPending || isDescriptionEmpty"
        >Save</p-button
      >
    </div>
  </form>
</template>

<script>
import { mapState } from 'vuex';

import Multiselect from '@/components/common/Multiselect';
import TextField from '@/components/common/TextField';
import Button from '@/components/common/Button';
import Checkbox from '@/components/common/Checkbox';
import JsonTree from '@/components/common/JsonTree';

export default {
  components: {
    'p-text-field': TextField,
    'p-button': Button,
    'p-checkbox': Checkbox,
    'p-json-tree': JsonTree,
    Multiselect
  },
  props: {
    item: {
      type: Object,
      default: () => null
    },
    isRequestPending: {
      type: Boolean,
      default: false
    },
    isRequestFailed: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localItem: this.item
        ? { ...this.item }
        : {
            name: '',
            description: '',
            action: '',
            parameters: {},
            cache: false,
            systemPrompt: '',
            tags: [],
            organizations: [],
            models: []
          }
    };
  },
  computed: {
    ...mapState({
      organizationsCollection: s => s.organizations.collection,
      models: s => s.nlp.models
    }),
    modelOptions() {
      return this.models.map(m => m.id) || [];
    },
    organizationsOptions() {
      return this.organizationsCollection.map(o => o.id) ?? [];
    },
    hasParameters() {
      return this.localItem.parameters && Object.keys(this.localItem.parameters).length > 0;
    },
    isDescriptionEmpty() {
      return !this.localItem.description || this.localItem.description.trim() === '';
    }
  },
  watch: {
    item: {
      handler(newItem) {
        if (newItem) {
          this.localItem = { ...newItem };
        }
      },
      deep: true
    }
  },
  async created() {
    await Promise.all([this.$store.dispatch('organizations/getCollection'), this.$store.dispatch('nlp/getConfigurations')]);
  },
  methods: {
    getOrganizationName(value) {
      return this.organizationsCollection.find(o => o.id === value)?.name ?? 'Unknown Organization';
    },
    getModelLabel(id) {
      return this.models.find(m => m.id === id)?.name ?? 'Unknown Model';
    },
    async submit() {
      this.$emit('submit', {
        name: this.localItem.name,
        description: this.localItem.description,
        action: this.localItem.action,
        parameters: this.hasParameters ? this.$refs.jsonTree.getValue() : {},
        cache: this.localItem.cache,
        systemPrompt: this.localItem.systemPrompt,
        tags: this.localItem.tags,
        organizations: this.localItem.organizations,
        models: this.localItem.models
      });
    },
    tagAdded(tag) {
      if (!Array.isArray(this.localItem.tags)) {
        this.localItem.tags = [];
      }

      if (this.localItem.tags.includes(tag)) {
        return;
      }
      this.localItem.tags.push(tag);
    },
    onCancel() {
      this.$emit('cancel');
    }
  }
};
</script>

<style lang="scss" scoped>
.create-or-edit-tool-form {
  width: 100%;
  height: 100%;
  background: var(--theme-surface);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .form-content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    padding-bottom: 1rem;
  }

  .form-row {
    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }

    > label {
      font-weight: 500;
      font-size: 0.75rem;
      letter-spacing: 0.025em;
      display: block;
      margin-bottom: 5px;
    }
  }

  .form-content > * {
    &:not(:last-child) {
      margin-bottom: 1.5rem;
    }
  }

  .button-container {
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 1rem 2rem;
    background: var(--theme-surface);
    border-top: 1px solid var(--theme-on-background-dark);
  }

  .error {
    font-size: 0.8rem;
    color: var(--theme-error);
    text-align: left;
    padding: 0.25rem 0;
  }

  .no-parameters-placeholder {
    padding: 1rem;
    text-align: center;
    color: var(--theme-on-surface);
    opacity: 0.6;
    font-style: italic;
  }
}
</style>
